import { useMemo } from "react";
import { Keyboard, Layers, PenLine } from "lucide-react";
import type { Dash, FillStyle, FontKey, HeadKind, PenKind, Route, ShapeKind } from "../../shared/model";
import { useBoard, useEditor, useStyle, useUi } from "../editor/react/hooks";
import { inkPath, PENS } from "../editor/ink";
import { adaptInk, FILLS, FONTS, HIGHLIGHTS, INKS, NOTE_PAPERS, PAPERS, TAPES } from "../editor/style";
import { shapeOutlinePath } from "../editor/sketch";
import { Chip, CustomColor, Dot, MarkerSwatch, Panel, Stepper, WavySlider } from "../ui/kit";

const PEN_ORDER: PenKind[] = ["fountain", "ballpoint", "pencil", "marker", "brush"];

function sampleStroke(pen: PenKind): string {
  const pts: number[] = [];
  for (let i = 0; i <= 40; i++) {
    const t = i / 40;
    pts.push(6 + t * 50, 17 + Math.sin(t * Math.PI * 2) * 7 * (1 - t * 0.2), 0.25 + 0.75 * Math.sin(t * Math.PI));
  }
  const size = pen === "marker" ? 5 : pen === "brush" ? 6.5 : pen === "highlighter" ? 10 : 3;
  return inkPath({ pts, size, pen, sim: false });
}

export function PenSample({ pen, color }: { pen: PenKind; color: string }) {
  const d = useMemo(() => sampleStroke(pen), [pen]);
  return (
    <svg viewBox="0 0 62 34" width="56" height="30" aria-hidden="true">
      <path d={d} fill={color} opacity={PENS[pen].opacity} />
    </svg>
  );
}

export function SizeDots({ sizes, value, onChange, color = "var(--ink)" }: { sizes: number[]; value: number; onChange: (s: number) => void; color?: string }) {
  const max = Math.max(...sizes);
  return (
    <div className="size-dots" role="group" aria-label="Size">
      {sizes.map((s) => (
        <button key={s} type="button" className={"size-dot" + (Math.abs(s - value) < 0.01 ? " active" : "")} onClick={() => onChange(s)} aria-label={"Size " + s} data-tip={"Size " + s}>
          <span style={{ width: 4 + (s / max) * 14, height: 4 + (s / max) * 14, background: color }} />
        </button>
      ))}
    </div>
  );
}

export function InkMarkers({ value, onChange, dark }: { value: string; onChange: (c: string) => void; dark?: boolean }) {
  const inks = dark ? [INKS[INKS.length - 1], ...INKS.slice(1, -1)] : INKS.slice(0, -1);
  return (
    <div className="markers" role="group" aria-label="Ink colour">
      {inks.map((s) => (
        <MarkerSwatch key={s.color} color={s.color} label={s.name} active={value.toLowerCase() === s.color} onClick={() => onChange(s.color)} />
      ))}
      <CustomColor value={value} onChange={onChange} />
    </div>
  );
}

export const SHAPE_KINDS: { kind: ShapeKind; label: string }[] = [
  { kind: "rect", label: "Rectangle" },
  { kind: "roundrect", label: "Rounded" },
  { kind: "ellipse", label: "Ellipse" },
  { kind: "diamond", label: "Decision" },
  { kind: "triangle", label: "Triangle" },
  { kind: "hexagon", label: "Hexagon" },
  { kind: "parallelogram", label: "Data" },
  { kind: "cylinder", label: "Database" },
  { kind: "cloud", label: "Cloud" },
  { kind: "star", label: "Star" },
  { kind: "heart", label: "Heart" },
  { kind: "speech", label: "Speech bubble" }
];

export function ShapeGlyph({ kind }: { kind: ShapeKind }) {
  return (
    <svg viewBox="-2 -2 28 24" width="26" height="22" aria-hidden="true">
      <path d={shapeOutlinePath(kind, 24, 20)} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

export const FILL_STYLES: { key: FillStyle; label: string }[] = [
  { key: "none", label: "No fill" },
  { key: "solid", label: "Solid" },
  { key: "hachure", label: "Hatched" },
  { key: "cross", label: "Cross-hatched" },
  { key: "dots", label: "Dotted" }
];

export function FillGlyph({ kind }: { kind: FillStyle }) {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <defs>
        <clipPath id={"fg-" + kind}>
          <rect x="2" y="2" width="16" height="16" rx="2" />
        </clipPath>
      </defs>
      <g clipPath={`url(#fg-${kind})`} stroke="currentColor" strokeWidth="1.3">
        {kind === "solid" && <rect x="2" y="2" width="16" height="16" fill="var(--caramel)" stroke="none" />}
        {(kind === "hachure" || kind === "cross") && [0, 5, 10, 15, 20, 25].map((o) => <line key={o} x1={o - 8} y1="20" x2={o + 4} y2="0" />)}
        {kind === "cross" && [0, 5, 10, 15, 20, 25].map((o) => <line key={"c" + o} x1={o - 8} y1="0" x2={o + 4} y2="20" />)}
        {kind === "dots" && [4, 9, 14].flatMap((x) => [4, 9, 14].map((y) => <circle key={x + "-" + y} cx={x + 1} cy={y + 1} r="1.1" fill="currentColor" stroke="none" />))}
      </g>
      <rect x="2" y="2" width="16" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

export function DashGlyph({ dash }: { dash: Dash }) {
  return (
    <svg viewBox="0 0 30 10" width="30" height="10" aria-hidden="true">
      <line x1="2" y1="5" x2="28" y2="5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeDasharray={dash === "dashed" ? "6 4" : dash === "dotted" ? "0.5 4.5" : undefined} />
    </svg>
  );
}

export function HeadGlyph({ head, flip }: { head: HeadKind; flip?: boolean }) {
  const t = flip ? "scale(-1 1) translate(-30 0)" : undefined;
  return (
    <svg viewBox="0 0 30 14" width="30" height="14" aria-hidden="true">
      <g transform={t} stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="7" x2="25" y2="7" />
        {head === "arrow" && <path d="M19 2.5 L26 7 L19 11.5" />}
        {head === "triangle" && <path d="M19 2.5 L27 7 L19 11.5 Z" fill="currentColor" />}
        {head === "dot" && <circle cx="24" cy="7" r="3.4" fill="currentColor" />}
        {head === "bar" && <line x1="26" y1="2" x2="26" y2="12" />}
        {head === "diamond" && <path d="M19 7 L23 3 L27.5 7 L23 11 Z" fill="currentColor" />}
      </g>
    </svg>
  );
}

export const ROUTES: { key: Route; label: string; d: string }[] = [
  { key: "straight", label: "Straight", d: "M3 17 L25 3" },
  { key: "curve", label: "Curved", d: "M3 17 Q 6 3 25 3" },
  { key: "elbow", label: "Elbow", d: "M3 17 H14 V3 H25" }
];

export const FONT_KEYS: FontKey[] = ["hand", "print", "marker", "type", "serif", "sans", "mono"];

export function FontChips({ value, onChange, only }: { value: FontKey; onChange: (f: FontKey) => void; only?: FontKey[] }) {
  return (
    <div className="chips" role="group" aria-label="Font">
      {(only ?? FONT_KEYS).map((k) => (
        <Chip key={k} active={value === k} onClick={() => onChange(k)} style={{ fontFamily: FONTS[k].family, fontSize: 15 * FONTS[k].scale }}>
          {FONTS[k].label}
        </Chip>
      ))}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <span className="tool-hint">{children}</span>;
}

/** A tab on top of the options bar naming the layer new things land on; tap to open the layers panel. */
function LayerTab({ onOpen }: { onOpen: () => void }) {
  const layer = useBoard((_, e) => {
    const id = e.activeLayerId();
    const l = e.layerById(id);
    return l ? { name: l.name, count: e.layers().length, blocked: !l.visible || !!l.locked } : null;
  });
  if (!layer || layer.count < 2) return null;
  return (
    <button type="button" className={"layer-tab" + (layer.blocked ? " blocked" : "")} onClick={onOpen} aria-label={`Active layer: ${layer.name}. Open layers`} data-tip="Active layer · tap for Layers" data-tip-side="top">
      <Layers size={14} aria-hidden="true" />
      <span>{layer.name}</span>
    </button>
  );
}

const touchOnly = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches && !window.matchMedia?.("(any-pointer: fine)").matches;

/** Pen or keyboard: on a phone (no pencil yet) or while typing, a two-way switch above the options bar. */
function WriteSwitch() {
  const ed = useEditor();
  const keyboard = useUi((_, e) => e.keyboardMode);
  const show = useUi((_, e) => e.keyboardMode || (touchOnly && !e.settings.penSeen));
  if (!show) return null;
  return (
    <div className="write-switch" role="radiogroup" aria-label="Write with">
      <button type="button" role="radio" aria-checked={!keyboard} className={"write-opt" + (keyboard ? "" : " active")} onClick={() => ed.setWriteWith("pen")}>
        <PenLine size={17} aria-hidden="true" /> Pen
      </button>
      <button type="button" role="radio" aria-checked={keyboard} className={"write-opt" + (keyboard ? " active" : "")} onClick={() => ed.setWriteWith("keyboard")}>
        <Keyboard size={18} aria-hidden="true" /> Keyboard
      </button>
    </div>
  );
}

export function ToolOptions({ onOpenStickers, onOpenLayers }: { onOpenStickers: () => void; onOpenLayers: () => void }) {
  const ed = useEditor();
  const tool = useUi((ui) => ui.tool);
  const pencilOnly = useUi((_, e) => e.pencilOnly);
  const dark = useUi((_, e) => !!PAPERS[e.bg.paper]?.dark);
  const st = useStyle((s) => s);
  const set = ed.setStyle.bind(ed);
  const layerCount = useBoard((_, e) => e.layers().length);

  let body: React.ReactNode = null;
  switch (tool) {
    case "pen": {
      const pen = st.pen === "highlighter" ? "fountain" : st.pen;
      const base = PENS[pen].size;
      body = (
        <>
          <div className="pen-kinds" role="group" aria-label="Pen">
            {PEN_ORDER.map((k) => (
              <button key={k} type="button" className={"pen-kind" + (pen === k ? " active" : "")} onClick={() => set({ pen: k })} data-tip={PENS[k].label} aria-label={PENS[k].label} aria-pressed={pen === k}>
                <PenSample pen={k} color={adaptInk(st.inkColor, false)} />
              </button>
            ))}
          </div>
          <span className="bar-sep" />
          <InkMarkers value={st.inkColor} onChange={(c) => set({ inkColor: c })} dark={dark} />
          <span className="bar-sep" />
          <SizeDots sizes={[base * 0.45, base * 0.75, base, base * 1.7, base * 2.8].map((v) => Math.round(v * 10) / 10)} value={st.penSize[pen]} onChange={(s) => set({ penSize: { ...st.penSize, [pen]: s } })} color={adaptInk(st.inkColor, false)} />
        </>
      );
      break;
    }
    case "highlighter":
      body = (
        <>
          <div className="dots" role="group" aria-label="Highlighter colour">
            {HIGHLIGHTS.map((h) => (
              <Dot key={h.color} color={h.color} label={h.name} active={st.hlColor === h.color} onClick={() => set({ hlColor: h.color })} />
            ))}
            <CustomColor value={st.hlColor} onChange={(c) => set({ hlColor: c })} />
          </div>
          <span className="bar-sep" />
          <SizeDots sizes={[10, 18, 28]} value={st.penSize.highlighter} onChange={(s) => set({ penSize: { ...st.penSize, highlighter: s } })} color={st.hlColor} />
          <Hint>hold still at the end to straighten</Hint>
        </>
      );
      break;
    case "eraser":
      body = (
        <>
          <div className="chips">
            <Chip active={st.eraserMode === "stroke"} onClick={() => set({ eraserMode: "stroke" })}>whole strokes</Chip>
            <Chip active={st.eraserMode === "precise"} onClick={() => set({ eraserMode: "precise" })}>precise</Chip>
          </div>
          <span className="bar-sep" />
          <SizeDots sizes={[8, 14, 26, 44]} value={st.eraserSize} onChange={(s) => set({ eraserSize: s })} />
          {layerCount > 1 ? (
            <>
              <span className="bar-sep" />
              <div className="chips" role="group" aria-label="Erase on">
                <Chip active={!st.eraseAllLayers} onClick={() => set({ eraseAllLayers: false })}>this layer</Chip>
                <Chip active={st.eraseAllLayers} onClick={() => set({ eraseAllLayers: true })}>all layers</Chip>
              </div>
            </>
          ) : (
            <Hint>or scribble over ink with the pen</Hint>
          )}
        </>
      );
      break;
    case "shape":
      body = (
        <>
          <div className="shape-kinds" role="group" aria-label="Shape">
            {SHAPE_KINDS.map((s) => (
              <button key={s.kind} type="button" className={"glyph-btn" + (st.shapeKind === s.kind ? " active" : "")} onClick={() => set({ shapeKind: s.kind })} data-tip={s.label} aria-label={s.label}>
                <ShapeGlyph kind={s.kind} />
              </button>
            ))}
          </div>
          <span className="bar-sep" />
          <InkMarkers value={st.stroke} onChange={(c) => set({ stroke: c, textColor: c })} dark={dark} />
          <span className="bar-sep" />
          <div className="glyph-row">
            {FILL_STYLES.map((f) => (
              <button key={f.key} type="button" className={"glyph-btn" + (st.fillStyle === f.key ? " active" : "")} onClick={() => set({ fillStyle: f.key })} data-tip={f.label} aria-label={f.label}>
                <FillGlyph kind={f.key} />
              </button>
            ))}
          </div>
          {st.fillStyle !== "none" && (
            <div className="dots">
              {FILLS.slice(0, 8).map((f) => (
                <Dot key={f.color} color={f.color} label={f.name} active={st.fill === f.color} onClick={() => set({ fill: f.color })} />
              ))}
            </div>
          )}
        </>
      );
      break;
    case "line":
      body = (
        <>
          <div className="glyph-row">
            {ROUTES.map((r) => (
              <button key={r.key} type="button" className={"glyph-btn" + (st.route === r.key ? " active" : "")} onClick={() => set({ route: r.key })} data-tip={r.label} aria-label={r.label}>
                <svg viewBox="0 0 28 20" width="28" height="20" aria-hidden="true">
                  <path d={r.d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ))}
          </div>
          <span className="bar-sep" />
          <div className="glyph-row">
            {(["none", "arrow", "triangle", "dot"] as HeadKind[]).map((h) => (
              <button key={h} type="button" className={"glyph-btn" + (st.endHead === h ? " active" : "")} onClick={() => set({ endHead: h })} data-tip={"End: " + h} aria-label={"End " + h}>
                <HeadGlyph head={h} />
              </button>
            ))}
          </div>
          <span className="bar-sep" />
          <InkMarkers value={st.stroke} onChange={(c) => set({ stroke: c })} dark={dark} />
          <span className="bar-sep" />
          <div className="glyph-row">
            {(["solid", "dashed", "dotted"] as Dash[]).map((d) => (
              <button key={d} type="button" className={"glyph-btn" + (st.dash === d ? " active" : "")} onClick={() => set({ dash: d })} data-tip={d} aria-label={d}>
                <DashGlyph dash={d} />
              </button>
            ))}
          </div>
        </>
      );
      break;
    case "text":
      body = (
        <>
          <FontChips value={st.font} onChange={(f) => set({ font: f })} />
          <span className="bar-sep" />
          <Stepper value={st.fontSize} onChange={(v) => set({ fontSize: v })} min={8} max={200} step={4} label="text size" />
          <span className="bar-sep" />
          <InkMarkers value={st.textColor} onChange={(c) => set({ textColor: c })} dark={dark} />
        </>
      );
      break;
    case "note":
      body = (
        <>
          <div className="dots" role="group" aria-label="Note paper">
            {NOTE_PAPERS.map((p) => (
              <Dot key={p.color} color={p.color} label={p.name} active={st.notePaper === p.color} onClick={() => set({ notePaper: p.color })} />
            ))}
          </div>
          <span className="bar-sep" />
          <div className="dots" role="group" aria-label="Tape">
            <Chip active={st.tape === null} onClick={() => set({ tape: null })}>no tape</Chip>
            {TAPES.map((t) => (
              <Dot key={t.color} color={t.color} label={t.name} active={st.tape === t.color} onClick={() => set({ tape: t.color })} ring />
            ))}
          </div>
          <Hint>tap to stick a note</Hint>
        </>
      );
      break;
    case "card":
      body = (
        <>
          <div className="dots" role="group" aria-label="Card paper">
            {["#fbf6ea", "#fdfbf6", "#f3e6cf", "#e9efe0", "#f6e6e0", "#e6edf3"].map((c) => (
              <Dot key={c} color={c} label="Card paper" active={st.cardPaper === c} onClick={() => set({ cardPaper: c })} />
            ))}
          </div>
          <Hint>markdown · [[link a board]] · - [ ] tasks · $math$</Hint>
        </>
      );
      break;
    case "sticker":
      body = (
        <>
          <button type="button" className="btn btn-plain" onClick={onOpenStickers}>
            Open sticker book
          </button>
          <Hint>{st.stickerKey ? "tap the page to stick it · Esc when done" : "pick a sticker first"}</Hint>
        </>
      );
      break;
    case "frame":
      body = <Hint>drag out a page · frames become slides in Present</Hint>;
      break;
    case "laser":
      body = <Hint>point things out · the trail fades</Hint>;
      break;
    case "lasso":
      body = <Hint>draw a loop around ink to select it · drag inside to move</Hint>;
      break;
    case "hand":
      body = <Hint>drag to move around · pinch or ⌘-scroll to zoom</Hint>;
      break;
    default:
      body = <Hint>{pencilOnly ? "pencil draws · fingers pan, pinch and tap" : "tap to select · drag to box-select · double-tap to edit"}</Hint>;
  }

  const drawing = ["pen", "highlighter", "shape", "line", "text", "note", "card", "frame", "sticker"].includes(tool) || (tool === "eraser" && !st.eraseAllLayers);
  return (
    <div className="tool-dock">
      <WriteSwitch />
      {drawing && <LayerTab onOpen={onOpenLayers} />}
      <Panel className="tool-options" seed={"opts-" + tool}>
        {body}
      </Panel>
    </div>
  );
}

export function NeatnessSlider({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return <WavySlider value={value} onChange={onChange} label="Neatness" left="neat" right="scrawl" />;
}
